import React, { useState, useRef } from 'react';
import { Container, Card, Row, Col, Button, Form, Alert, Table, Badge, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { fires } from '../../firebase';
import Papa from 'papaparse';
import './AdminEmailSender.css';

const AdminEmailSender = () => {
  const { currentUser } = useAuth();
  const [csvFile, setCsvFile] = useState(null);
  const [emailData, setEmailData] = useState([]);
  const [templateVariant, setTemplateVariant] = useState('variant1');
  const [isSending, setIsSending] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0, failed: 0 });
  const [sendResults, setSendResults] = useState([]);
  const fileInputRef = useRef(null);

  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);

  // Check if user exists in admin collection
  React.useEffect(() => {
    const checkAdminAccess = async () => {
      if (!currentUser) {
        setIsCheckingAccess(false);
        return;
      }

      try {
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        setIsAdmin(adminDoc.exists);
      } catch (error) {
        console.error('Error checking admin access:', error);
        setIsAdmin(false);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkAdminAccess();
  }, [currentUser]);

  if (isCheckingAccess) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Checking access...</p>
        </div>
      </Container>
    );
  }

  if (!currentUser || !isAdmin) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>You don't have permission to access this page. Please contact an administrator.</p>
        </Alert>
      </Container>
    );
  }

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      parseCSV(file);
    } else {
      displayToast('Please select a valid CSV file', 'danger');
    }
  };

  const parseCSV = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          displayToast('Error parsing CSV file', 'danger');
          return;
        }
        
        const emails = results.data.map((row, index) => {
          // Look for email field in various possible column names
          const email = row.email || row.Email || row.EMAIL || row.mail || row.Mail || row.MAIL || 
                       row['email address'] || row['Email Address'] || row['EMAIL ADDRESS'] ||
                       Object.values(row)[0]; // Fallback to first column
          
          return {
            id: index + 1,
            email: email?.trim(),
            name: row.name || row.Name || row.NAME || row['full name'] || row['Full Name'] || row['FULL NAME'] || '',
            isValid: email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
          };
        }).filter(item => item.email); // Remove empty rows

        setEmailData(emails);
        displayToast(`Successfully loaded ${emails.length} email addresses`, 'success');
      },
      error: (error) => {
        displayToast('Error reading CSV file', 'danger');
        console.error('CSV parsing error:', error);
      }
    });
  };



  const handleSendEmails = async () => {
    if (emailData.length === 0) {
      displayToast('Please upload a CSV file with email addresses', 'warning');
      return;
    }

    const validEmails = emailData.filter(item => item.isValid);
    if (validEmails.length === 0) {
      displayToast('No valid email addresses found', 'warning');
      return;
    }

    setIsSending(true);
    setSendProgress({ sent: 0, total: validEmails.length, failed: 0 });
    setSendResults([]);

    try {
      // Extract email addresses from valid emails
      const emailList = validEmails.map(item => item.email);
      
      // Map template variant to source (A or B)
      const source = templateVariant === 'variant1' ? 'A' : 'B';
      
      const response = await sendEmailAPI(emailList, source);
      
      if (response.success) {
        // All emails sent successfully
        const results = validEmails.map(emailItem => ({
          email: emailItem.email,
          name: emailItem.name,
          status: 'success',
          message: 'Email sent successfully'
        }));
        
        setSendProgress({ sent: validEmails.length, total: validEmails.length, failed: 0 });
        setSendResults(results);
        displayToast(`Successfully sent ${validEmails.length} emails`, 'success');
      } else {
        // Failed to send emails
        const results = validEmails.map(emailItem => ({
          email: emailItem.email,
          name: emailItem.name,
          status: 'failed',
          message: response.message || 'Failed to send emails'
        }));
        
        setSendProgress({ sent: 0, total: validEmails.length, failed: validEmails.length });
        setSendResults(results);
        displayToast(`Failed to send emails: ${response.message}`, 'danger');
      }
    } catch (error) {
      // Network or other error
      const results = validEmails.map(emailItem => ({
        email: emailItem.email,
        name: emailItem.name,
        status: 'failed',
        message: error.message || 'Network error'
      }));
      
      setSendProgress({ sent: 0, total: validEmails.length, failed: validEmails.length });
      setSendResults(results);
      displayToast(`Error sending emails: ${error.message}`, 'danger');
    }

    setIsSending(false);
  };

  const sendEmailAPI = async (emails, source) => {
    const apiUrl = 'https://v2serviceapisendorgcoldemails-650318705789.us-central1.run.app';
    
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          emails: emails,
          source: source
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'API request failed');
      }

      return { success: true, data };
    } catch (error) {
      console.error('Email API error:', error);
      return { success: false, message: error.message };
    }
  };

  const displayToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const clearData = () => {
    setCsvFile(null);
    setEmailData([]);
    setSendProgress({ sent: 0, total: 0, failed: 0 });
    setSendResults([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'email,name\njohn@example.com,John Doe\njane@example.com,Jane Smith';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'email_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const validEmails = emailData.filter(item => item.isValid);
  const invalidEmails = emailData.filter(item => !item.isValid);

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h4 className="mb-0">
                <i className="fas fa-envelope me-2"></i>
                Admin Email List Manager
              </h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col lg={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Upload Email List (CSV)</h6>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Select CSV File</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".csv"
                          onChange={handleFileUpload}
                          ref={fileInputRef}
                        />
                        <Form.Text className="text-muted">
                          CSV should contain columns: email, name (optional)
                        </Form.Text>
                      </Form.Group>
                      
                      <div className="d-flex gap-2 mb-3">
                        <Button variant="outline-primary" size="sm" onClick={downloadTemplate}>
                          <i className="fas fa-download me-1"></i>
                          Download Template
                        </Button>
                        {emailData.length > 0 && (
                          <Button variant="outline-secondary" size="sm" onClick={clearData}>
                            <i className="fas fa-trash me-1"></i>
                            Clear Data
                          </Button>
                        )}
                      </div>

                      {emailData.length > 0 && (
                        <div className="mb-3">
                          <h6>Email Summary:</h6>
                          <div className="d-flex gap-3">
                            <Badge bg="success">Valid: {validEmails.length}</Badge>
                            {invalidEmails.length > 0 && (
                              <Badge bg="warning">Invalid: {invalidEmails.length}</Badge>
                            )}
                            <Badge bg="info">Total: {emailData.length}</Badge>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>

                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Send Emails</h6>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Template Variant</Form.Label>
                        <Form.Select
                          value={templateVariant}
                          onChange={(e) => setTemplateVariant(e.target.value)}
                        >
                          <option value="variant1">Email Variant A</option>
                          <option value="variant2">Email Variant B</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Select which email variant to use (A or B)
                        </Form.Text>
                      </Form.Group>

                      <div className="d-flex gap-2">
                        <Button
                          variant="primary"
                          onClick={handleSendEmails}
                          disabled={isSending || validEmails.length === 0}
                        >
                          {isSending ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane me-1"></i>
                              Send Emails ({validEmails.length})
                            </>
                          )}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={6}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Email List Preview</h6>
                    </Card.Header>
                    <Card.Body>
                      {emailData.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <i className="fas fa-upload fa-2x mb-3"></i>
                          <p>Upload a CSV file to see the email list</p>
                        </div>
                      ) : (
                        <div>
                          <div className="mb-3">
                            <h6>Valid Emails ({validEmails.length})</h6>
                            <Table size="sm" striped>
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Email</th>
                                  <th>Name</th>
                                </tr>
                              </thead>
                              <tbody>
                                {validEmails.slice(0, 10).map((item) => (
                                  <tr key={item.id}>
                                    <td>{item.id}</td>
                                    <td>{item.email}</td>
                                    <td>{item.name || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                            {validEmails.length > 10 && (
                              <small className="text-muted">
                                Showing first 10 of {validEmails.length} valid emails
                              </small>
                            )}
                          </div>

                          {invalidEmails.length > 0 && (
                            <div>
                              <h6>Invalid Emails ({invalidEmails.length})</h6>
                              <Table size="sm" striped>
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Email</th>
                                    <th>Name</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {invalidEmails.slice(0, 5).map((item) => (
                                    <tr key={item.id}>
                                      <td>{item.id}</td>
                                      <td className="text-danger">{item.email}</td>
                                      <td>{item.name || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                              {invalidEmails.length > 5 && (
                                <small className="text-muted">
                                  Showing first 5 of {invalidEmails.length} invalid emails
                                </small>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </Card.Body>
                  </Card>


                </Col>
              </Row>
            </Card.Body>
                                              </Card>

                  {sendResults.length > 0 && (
                    <Card className="mt-4">
                      <Card.Header>
                        <h6 className="mb-0">Send Results</h6>
                      </Card.Header>
                      <Card.Body>
                        <div className="mb-3">
                          <div className="d-flex gap-3">
                            <Badge bg="success">Sent: {sendProgress.sent}</Badge>
                            <Badge bg="danger">Failed: {sendProgress.failed}</Badge>
                            <Badge bg="info">Total: {sendProgress.total}</Badge>
                          </div>
                        </div>
                        
                        <Table size="sm" striped>
                          <thead>
                            <tr>
                              <th>Email</th>
                              <th>Status</th>
                              <th>Message</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sendResults.slice(0, 10).map((result, index) => (
                              <tr key={index}>
                                <td>{result.email}</td>
                                <td>
                                  <Badge bg={result.status === 'success' ? 'success' : 'danger'}>
                                    {result.status}
                                  </Badge>
                                </td>
                                <td className="small">{result.message}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        {sendResults.length > 10 && (
                          <small className="text-muted">
                            Showing first 10 of {sendResults.length} results
                          </small>
                        )}
                      </Card.Body>
                    </Card>
                  )}
                </Col>
              </Row>

      <ToastContainer position="top-end" className="p-3">
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={5000} autohide>
          <Toast.Header closeButton>
            <strong className="me-auto">Email Sender</strong>
          </Toast.Header>
          <Toast.Body className={toastVariant === 'danger' ? 'text-danger' : ''}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};

export default AdminEmailSender;
